class CustomSliderComponent extends HTMLElement {
  constructor() {
    super();

    this.wrapper = this.querySelector('.slider__wrapper');
    this.list = this.querySelector('.slider__list');
    this.originalItems = this.querySelectorAll('.slider__item');
    this.navPrev = this.querySelector('.slider__nav--prev');
    this.navNext = this.querySelector('.slider__nav--next');
    this.pagination = this.querySelector('.slider__pagination');

    this.startX = 0;
    this.wasDragging = false;
    this.isDragging = false;
    this.currentIndex = 0;
    this.autoPlayInterval = null;
    this.isSlideScrolling = false;

    this.autoPlay = this.dataset.autoPlay === 'true';
    this.looping = this.dataset.looping === 'true';
    this.scrollSpeed = (this.dataset.scrollSpeed * 1000) || 3000;
    this.transitionSpeed = this.dataset.transitionSpeed || 1;
    this.scrollType = this.dataset.scrollType || 'per_page';

    this.updateSettings();

    if (this.originalItems.length <= this.perPage) {
      // Not enough items to slide, so disable slider functionality
      this.wrapper.style.overflow = 'visible';
      this.list.style.transform = 'none';
      if (this.navPrev) this.navPrev.style.display = 'none';
      if (this.navNext) this.navNext.style.display = 'none';
      if (this.pagination) this.pagination.style.display = 'none';
      return;
    }


    if (this.looping) this.cloneSlides();

    this.items = this.querySelectorAll('.slider__item');

    this.bindEvents();
    this.createDots();
    this.snapToCurrent(false);
    this.updateNavButtons();
    this.updateSlideClasses();

    if (this.autoPlay) {
      this.startAutoplay();
    }

    window.addEventListener('resize', () => {
      this.updateSettings();
      if (this.looping) this.resetClones();
      this.items = this.querySelectorAll('.slider__item');
      this.createDots();
      this.snapToCurrent(false);
      this.updateSlideClasses();
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))

    this.resizeObserver = new ResizeObserver(() => {
      this.updateSettings();
      if (this.looping) this.resetClones();
      this.items = this.querySelectorAll('.slider__item');
      this.createDots();
      this.snapToCurrent(false);
      this.updateSlideClasses();
    });
    this.resizeObserver.observe(this.wrapper);

    this.list.addEventListener('transitionend', () => {
      if (!this.looping) return;

      const virtualLength = this.items.length - 2 * this.cloneCount;

      if (this.currentIndex < 0) {
        this.currentIndex = virtualLength + this.currentIndex;
        this.snapToCurrent(false);
      } else if (this.currentIndex >= virtualLength) {
        this.currentIndex = this.currentIndex - virtualLength;
        this.snapToCurrent(false);
      }

      this.updateDots();
      this.updateSlideClasses();
    });
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Pause autoplay when the tab is hidden
      this.stopAutoplay();
    } else {
      // Resume autoplay and reinitialize slider when the tab becomes visible
      this.updateSettings();
      if (this.looping) this.resetClones();
      this.items = this.querySelectorAll('.slider__item');
      this.createDots();
      this.snapToCurrent(false);
      this.updateSlideClasses();
      if (this.autoPlay) {
        this.startAutoplay();
      }
    }
  }

  stopAutoplay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  updateSettings() {
    const isDesktop = window.innerWidth >= 750;
    this.perPage = parseInt(isDesktop ? this.dataset.perPageDesktop : this.dataset.perPage) || 1;
    this.gap = parseInt(isDesktop ? this.dataset.gapDesktop : this.dataset.gap) || 0;
    this.slideWidth = this.originalItems[0]?.offsetWidth || 0;
    this.scrollAmount = this.scrollType === 'per_item' ? 1 : this.perPage;

    this.cloneCount = Math.max(this.perPage, this.scrollAmount); // Clone enough items for smooth looping

    const totalItems = this.originalItems.length;
    this.totalPages = Math.ceil(totalItems / this.scrollAmount);
  }

  resetClones() {
    this.list.querySelectorAll('.clone').forEach(clone => clone.remove());
    this.cloneSlides();
  }

  cloneSlides() {
    const fragmentStart = document.createDocumentFragment();
    const fragmentEnd = document.createDocumentFragment();
    const len = this.originalItems.length;

    for (let i = 0; i < this.cloneCount; i++) {
      const cloneStart = this.originalItems[len - 1 - i].cloneNode(true);
      cloneStart.classList.add('clone');
      fragmentStart.insertBefore(cloneStart, fragmentStart.firstChild);

      const cloneEnd = this.originalItems[i % len].cloneNode(true);
      cloneEnd.classList.add('clone');
      fragmentEnd.appendChild(cloneEnd);
    }

    this.list.prepend(fragmentStart);
    this.list.append(fragmentEnd);
  }

  bindEvents() {
    this.wrapper.addEventListener('mousedown', this.startDrag.bind(this));
    this.wrapper.addEventListener('touchstart', this.startDrag.bind(this), { passive: true });

    // Use window to track drag even outside the component
    window.addEventListener('mouseup', this.endDrag.bind(this));
    window.addEventListener('touchend', this.endDrag.bind(this));
    window.addEventListener('mousemove', this.onDrag.bind(this));
    window.addEventListener('touchmove', this.onDrag.bind(this), { passive: false });

    // Cancel drag on scroll (to avoid jitter)
    window.addEventListener('scroll', this.cancelDrag.bind(this), true); // true to capture in bubbling phase
    window.addEventListener('mouseleave', this.cancelDrag.bind(this)); // fallback for mouse
    window.addEventListener('blur', this.cancelDrag.bind(this)); // if user switches tab or window

    if (this.navPrev) this.navPrev.addEventListener('click', (event) => {
      event.preventDefault(); 
      this.moveTo(this.currentIndex - this.scrollAmount);
    });
    if (this.navNext) this.navNext.addEventListener('click', (event) => {
      event.preventDefault(); 
      this.moveTo(this.currentIndex + this.scrollAmount);
    });

    this.wrapper.addEventListener('click', (e) => {
      if (this.wasDragging && e.target.closest('a')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });
  }

  cancelDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.snapToCurrent(); // return to current slide
    if (this.autoPlay) this.startAutoplay();
  }

  createDots() {
    if (!this.pagination) return;

    this.pagination.innerHTML = '';
    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'slider__dot';
      if (i === Math.floor(this.currentIndex / this.scrollAmount)) dot.classList.add('active');
      dot.addEventListener('click', () => this.moveTo(i * this.scrollAmount));
      this.pagination.appendChild(dot);
    }
  }

  startDrag(e) {
    if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);
    this.isDragging = true;
    this.wasDragging = false; // reset this on drag start
    this.startX = e.touches ? e.touches[0].clientX : e.clientX;
    this.list.style.transition = 'none';
  }

  onDrag(e) {
    if (!this.isDragging) return;

    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaX = currentX - this.startX;

    if (Math.abs(deltaX) > 5) {
      this.wasDragging = true; // flag drag if moved significantly
    }

    const offset = this.looping ? this.cloneCount : 0;
    const translate = -(this.currentIndex + offset) * (this.slideWidth + this.gap) + deltaX;
    this.list.style.transform = `translateX(${translate}px)`;
  }

  endDrag(e) {
    if (!this.isDragging) return;
    this.isDragging = false;

    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const deltaX = endX - this.startX;
    const threshold = (this.slideWidth + this.gap) / 4;

    if (deltaX < -threshold) {
      this.moveTo(this.currentIndex + this.scrollAmount);
    } else if (deltaX > threshold) {
      this.moveTo(this.currentIndex - this.scrollAmount);
    } else {
      this.snapToCurrent();
    }

    if (this.autoPlay) {
      this.startAutoplay();
    }
  }

  moveTo(index) {
    const maxValidIndex = Math.max(0, this.originalItems.length - this.perPage);

    if (!this.looping) {
      this.currentIndex = Math.max(0, Math.min(index, maxValidIndex));
    } else {
      this.currentIndex = index;
    }

    this.snapToCurrent();
  }

  snapToCurrent(animate = true) {
    const offset = this.looping ? this.cloneCount : 0;

    let translate;

    const totalItems = this.originalItems.length;
    const maxScrollIndex = totalItems - this.perPage;

    if (!this.looping && this.currentIndex > maxScrollIndex) {
      this.currentIndex = maxScrollIndex; // clamp to max start index
    }

    if (!this.looping && this.currentIndex + this.perPage > totalItems) {
      // On last slide with fewer items
      const visibleSlides = totalItems - this.currentIndex;
      const visibleWidth = visibleSlides * (this.slideWidth + this.gap) - this.gap;
      const fullWidth = this.perPage * (this.slideWidth + this.gap) - this.gap;
      const extraOffset = fullWidth - visibleWidth;

      translate = -(this.currentIndex + offset) * (this.slideWidth + this.gap) + extraOffset;
    } else {
      translate = -(this.currentIndex + offset) * (this.slideWidth + this.gap);
    }

    this.list.style.transition = animate ? `transform ${this.transitionSpeed}s ease` : 'none';
    this.list.style.transform = `translateX(${translate}px)`;

    this.updateDots();
    this.updateNavButtons();
    this.updateSlideClasses();
  }

  updateDots() {
    if (!this.pagination) return;

    const dots = this.pagination.querySelectorAll('.slider__dot');
    dots.forEach(dot => dot.classList.remove('active'));

    const totalItems = this.originalItems.length;

    let dotIndex;
    
    if (this.looping) {
      const visibleIndex = ((this.currentIndex % totalItems) + totalItems) % totalItems;
      dotIndex = Math.floor(visibleIndex / this.scrollAmount);
    } else {
      const maxScrollIndex = Math.max(0, totalItems - this.perPage);
      const calculatedDotIndex = Math.floor(this.currentIndex / this.scrollAmount);
      
      dotIndex = calculatedDotIndex;
      
      // Special handling for positions at or near the end
      if (this.currentIndex >= maxScrollIndex) {
        dotIndex = dots.length - 1;
      } else if (dotIndex >= dots.length) {
        dotIndex = dots.length - 1;
      }
      
      // Final bounds check
      dotIndex = Math.max(0, Math.min(dotIndex, dots.length - 1));
    }

    if (dots[dotIndex]) dots[dotIndex].classList.add('active');
  }

  // NEW METHOD: Update slide classes for active, prev, and next slides
  updateSlideClasses() {
    const totalItems = this.originalItems.length;
    
    // Remove existing classes from all original items
    this.originalItems.forEach(item => {
      item.classList.remove('active-slide', 'prev-slide', 'next-slide');
    });

    // Calculate the actual visible index (for looping sliders)
    let visibleIndex;
    if (this.looping) {
      visibleIndex = ((this.currentIndex % totalItems) + totalItems) % totalItems;
    } else {
      visibleIndex = this.currentIndex;
    }

    // For multi-slide display, we'll use the first visible slide as the "active" one
    const activeIndex = visibleIndex;
    const prevIndex = (activeIndex - 1 + totalItems) % totalItems;
    const nextIndex = (activeIndex + 1) % totalItems;

    // Add classes to the appropriate slides
    if (this.originalItems[activeIndex]) {
      this.originalItems[activeIndex].classList.add('active-slide');
    }
    
    if (this.originalItems[prevIndex] && prevIndex !== activeIndex) {
      this.originalItems[prevIndex].classList.add('prev-slide');
    }
    
    if (this.originalItems[nextIndex] && nextIndex !== activeIndex) {
      this.originalItems[nextIndex].classList.add('next-slide');
    }

    // For non-looping sliders, handle edge cases
    if (!this.looping) {
      // Don't add prev-slide class if we're at the beginning
      if (activeIndex === 0) {
        if (this.originalItems[prevIndex]) {
          this.originalItems[prevIndex].classList.remove('prev-slide');
        }
      }
      
      // Don't add next-slide class if we're at the end
      if (activeIndex === totalItems - 1) {
        if (this.originalItems[nextIndex]) {
          this.originalItems[nextIndex].classList.remove('next-slide');
        }
      }
    }
  }

  updateNavButtons() {
    if (!this.looping && this.navPrev && this.navNext) {
      const maxIndex = this.originalItems.length - this.perPage;

      this.navPrev.classList.toggle('disabled', this.currentIndex <= 0);
      this.navNext.classList.toggle('disabled', this.currentIndex >= maxIndex);
    }
  }

  startAutoplay() {
    if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);

    this.autoPlayInterval = setInterval(() => {
      if (!this.isDragging && !document.hidden) {
        this.moveTo(this.currentIndex + this.scrollAmount);
      }
    }, this.scrollSpeed);
  }
}

customElements.define('custom-slider-component', CustomSliderComponent);